/**
 * Zendure plug-in thuisbatterij landing page interactions.
 * Scoped to .zendure-plug-page and safe for Shopify theme-editor reloads.
 */
(() => {
  'use strict';

  if (window.__zendurePlugInBatteryLoaded) return;
  window.__zendurePlugInBatteryLoaded = true;

  const ROOT_SELECTOR = '.zendure-plug-page';

  function initModes(root) {
    const articles = Array.from(root.querySelectorAll('.mode-accordion article'));
    const images = Array.from(root.querySelectorAll('.mode-image img'));
    const label = root.querySelector('.mode-image-label');

    articles.forEach((article, index) => {
      const button = article.querySelector('button');
      if (!button || button.dataset.zendureBound === 'true') return;
      button.dataset.zendureBound = 'true';

      button.addEventListener('click', () => {
        articles.forEach((item, itemIndex) => {
          const active = itemIndex === index;
          item.classList.toggle('active', active);

          const itemButton = item.querySelector('button');
          const icon = itemButton && itemButton.querySelector('i');
          const detail = item.querySelector('.mode-detail-shell');

          if (itemButton) itemButton.setAttribute('aria-expanded', String(active));
          if (icon) icon.textContent = active ? '−' : '+';
          if (detail) detail.setAttribute('aria-hidden', String(!active));

          if (images[itemIndex]) {
            images[itemIndex].classList.toggle('active', active);
            images[itemIndex].setAttribute('aria-hidden', String(!active));
          }
        });

        const title = article.querySelector('b');
        if (label && title) label.textContent = title.textContent || '';
      });
    });
  }

  function initProductCarousel(root) {
    const track = root.querySelector('.product-track');
    if (!track) return;

    const controls = root.querySelectorAll('.carousel-tools button');
    const previous = controls[0];
    const next = controls[1];

    if (previous && previous.dataset.zendureBound !== 'true') {
      previous.dataset.zendureBound = 'true';
      previous.addEventListener('click', () => {
        track.scrollBy({ left: -420, behavior: 'smooth' });
      });
    }

    if (next && next.dataset.zendureBound !== 'true') {
      next.dataset.zendureBound = 'true';
      next.addEventListener('click', () => {
        track.scrollBy({ left: 420, behavior: 'smooth' });
      });
    }
  }

  function initChooser(root) {
    const quiz = root.querySelector('.quiz-card');
    const progress = root.querySelector('.progress span');
    const stepText = root.querySelector('.chooser-intro small');
    if (!quiz || !progress || !stepText || quiz.dataset.zendureBound === 'true') return;
    quiz.dataset.zendureBound = 'true';

    const fallbackProducts = {
      pro: { name: 'SolarFlow 2400 Pro', url: '#producten' },
      ac: { name: 'SolarFlow 2400 AC+', url: '#producten' },
      compact: { name: 'SolarFlow 1600 AC+', url: '#producten' }
    };
    const fallbackQuestions = [
      { key: 'solar', title: 'Heeft u al zonnepanelen?', options: [['yes','Ja, ik heb zonnepanelen'],['no','Nee, nog niet']] },
      { key: 'surplus', title: 'Hoeveel stroom levert u jaarlijks terug aan het net?', subtitle: 'U vindt dit op uw jaarafrekening. Weet u het niet? Kies dan de laatste optie.', options: [['low','Minder dan 1.500 kWh per jaar'],['mid','1.500–2.500 kWh per jaar'],['high','Meer dan 2.500 kWh per jaar'],['unknown','Ik weet het niet']] },
      { key: 'goal', title: 'Wat wilt u vooral bereiken met uw thuisbatterij?', options: [['budget','Voordelig beginnen'],['cheap','Laden bij lage stroomprijzen'],['self','Meer van mijn eigen zonnestroom gebruiken'],['backup','Noodstroom bij een stroomuitval']] }
    ];
    const fallbackNoSolarQuestions = [
      { key: 'usage', title: 'Hoeveel stroom gebruikt uw huishouden ongeveer per jaar?', subtitle: 'U vindt dit op uw jaarafrekening. Weet u het niet? Kies dan de laatste optie.', options: [['low','Minder dan 2.500 kWh per jaar'],['mid','2.500–4.000 kWh per jaar'],['high','Meer dan 4.000 kWh per jaar'],['unknown','Ik weet het niet']] },
      { key: 'goal', title: 'Wat wilt u vooral bereiken met uw thuisbatterij?', options: [['budget','Voordelig beginnen'],['cheap','Laden bij lage stroomprijzen'],['future','Voorbereid zijn op toekomstige zonnepanelen'],['backup','Noodstroom bij een stroomuitval']] }
    ];
    const fallbackLabels = {
      questionPrefix: 'Vraag', questionOf: 'van', recommendationStep: 'Uw aanbeveling',
      recommendationLabel: 'Aanbevolen', primaryButton: 'Bekijk product',
      resetButton: 'Opnieuw kiezen', powerLabel: 'Vermogen', capacityLabel: 'Capaciteit',
      connectionLabel: 'Aansluiting', offgridLabel: 'Off-grid', shortDisclaimer: 'Indicatief advies.'
    };

    let config = {};
    const configNode = root.querySelector('[data-zendure-quiz-config]');
    if (configNode) {
      try { config = JSON.parse(configNode.textContent || '{}'); }
      catch (error) { console.warn('Zendure quizconfiguratie kon niet worden gelezen.', error); }
    }

    const products = Object.assign({}, fallbackProducts, config.products || {});
    const baseQuestions = Array.isArray(config.questions) && config.questions.length ? config.questions : fallbackQuestions;
    const noSolarQuestions = Array.isArray(config.noSolarQuestions) && config.noSolarQuestions.length ? config.noSolarQuestions : fallbackNoSolarQuestions;
    const labels = Object.assign({}, fallbackLabels, config.labels || {});
    const solarQuestion = baseQuestions.find((question) => question.key === 'solar') || baseQuestions[0] || fallbackQuestions[0];
    const surplusQuestion = baseQuestions.find((question) => question.key === 'surplus') || baseQuestions[1] || fallbackQuestions[1];
    const solarGoalQuestion = baseQuestions.find((question) => question.key === 'goal') || baseQuestions[2] || fallbackQuestions[2];
    let step = 0;
    let answers = {};

    const escapeHtml = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[character]);

    function getQuestions() {
      return answers.solar === 'no'
        ? [solarQuestion, noSolarQuestions[0], noSolarQuestions[1]]
        : [solarQuestion, surplusQuestion, solarGoalQuestion];
    }

    function renderQuestion() {
      const questions = getQuestions();
      const question = questions[step];
      progress.style.width = (((step + 1) / questions.length) * 100) + '%';
      stepText.textContent = labels.questionPrefix + ' ' + (step + 1) + ' ' + labels.questionOf + ' ' + questions.length;
      quiz.innerHTML = '<div class="question"><h3>' + escapeHtml(question.title) + '</h3>' +
        (question.subtitle ? '<p>' + escapeHtml(question.subtitle) + '</p>' : '') +
        '<div class="option-list">' + (question.options || []).map(([value, text]) =>
          '<button type="button" data-value="' + escapeHtml(value) + '"><span>' + escapeHtml(text) + '</span><b aria-hidden="true">→</b></button>'
        ).join('') + '</div></div>';

      quiz.querySelectorAll('[data-value]').forEach((button) => {
        button.addEventListener('click', () => {
          answers[question.key] = button.dataset.value || '';
          step += 1;
          if (step < getQuestions().length) renderQuestion(); else renderResult();
        });
      });
    }

    function getRecommendationKey() {
      if (answers.solar === 'no') {
        if (answers.goal === 'future') return 'pro';
        if (answers.goal === 'backup') return 'ac';
        if (answers.goal === 'cheap' && answers.usage === 'high') return 'ac';
        return 'compact';
      }
      if (answers.goal === 'budget' || answers.surplus === 'low') return 'compact';
      return 'ac';
    }

    function findProductCard(product) {
      const target = String(product.name || '').toLowerCase().replace(/^zendures+/, '').trim();
      return Array.from(document.querySelectorAll('.product-card')).find((card) => {
        const title = String(card.querySelector('h3')?.textContent || '').toLowerCase().replace(/^zendures+/, '').trim();
        return title === target || title.includes(target) || target.includes(title);
      });
    }

    function getConfiguredCapacity(product) {
      const capacity = String(product.capacity || '').trim();
      const expansion = String(product.expansion || '').trim();
      if (!capacity) return '';
      if (!expansion || capacity.includes(expansion)) return capacity;
      const expansionText = /uitbreidbaar/i.test(expansion) ? expansion : 'uitbreidbaar ' + expansion;
      return capacity + ' · ' + expansionText;
    }

    function readProductCard(product) {
      const card = findProductCard(product);
      const configuredCapacity = getConfiguredCapacity(product);
      if (!card) {
        return {
          name: product.displayName || product.name || '',
          image: product.image || '',
          priceHtml: escapeHtml(product.price || ''),
          capacity: configuredCapacity,
          connection: product.connection || '',
          power: product.power || '',
          offgrid: product.offgrid || '',
          url: product.url || '#producten'
        };
      }

      const specs = Array.from(card.querySelectorAll('dl > div')).map((row) => ({
        label: String(row.querySelector('dt')?.textContent || '').trim(),
        value: String(row.querySelector('dd')?.textContent || '').trim()
      }));
      const image = card.querySelector('img');
      const link = card.querySelector('a.product-button');
      const cardTitle = String(card.querySelector('h3')?.textContent || product.name || '').trim();
      let automaticConnection = specs[1]?.value || '';
      if (/AC+/i.test(cardTitle)) automaticConnection = 'AC-gekoppeld · bestaande omvormer';

      return {
        name: product.displayName || cardTitle,
        image: product.image || (image ? (image.currentSrc || image.src || '') : ''),
        priceHtml: card.querySelector('.price')?.innerHTML || escapeHtml(product.price || ''),
        capacity: configuredCapacity || specs[0]?.value || '',
        connection: product.connection || automaticConnection,
        power: product.power || specs[2]?.value || '',
        offgrid: product.offgrid || specs[3]?.value || '',
        url: link?.href || product.url || '#producten'
      };
    }

    function specItem(label, value) {
      if (!value) return '';
      return '<li><b>' + escapeHtml(label) + '</b><span>' + escapeHtml(value) + '</span></li>';
    }

    function renderResult() {
      progress.style.width = '100%';
      stepText.textContent = labels.recommendationStep;
      const product = products[getRecommendationKey()] || products.compact || fallbackProducts.compact;
      const card = readProductCard(product);

      quiz.innerHTML = '<div class="result"><span class="result-label">' + escapeHtml(labels.recommendationLabel) + '</span>' +
        '<div class="result-main">' + (card.image ? '<img src="' + escapeHtml(card.image) + '" alt="' + escapeHtml(card.name) + '">' : '') +
        '<div><h3>' + escapeHtml(card.name) + '</h3><div class="price">' + card.priceHtml + '</div></div></div>' +
        '<ul class="result-specs">' +
        specItem(labels.powerLabel, card.power) +
        specItem(labels.capacityLabel, card.capacity) +
        specItem(labels.connectionLabel, card.connection) +
        specItem(labels.offgridLabel, card.offgrid) +
        '</ul><div class="result-actions">' +
        '<a class="button primary" href="' + escapeHtml(card.url) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(labels.primaryButton) + '</a>' +
        '<button class="button ghost" type="button" data-reset-quiz>' + escapeHtml(labels.resetButton) + '</button></div>' +
        '<small>' + escapeHtml(labels.shortDisclaimer) + '</small></div>';

      const reset = quiz.querySelector('[data-reset-quiz]');
      if (reset) reset.addEventListener('click', () => { step = 0; answers = {}; renderQuestion(); });
    }

    renderQuestion();
  }

  function initZendurePage(scope) {
    const roots = [];

    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope && scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      initModes(root);
      initProductCarousel(root);
      initChooser(root);
    });
  }

  function start() {
    initZendurePage(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initZendurePage(event.target);
  });
})();
